import { useId, useState } from 'react'
import { CollectedAtLine } from '@/components/SnapshotCard'
import { Badge } from '@/components/ui/badge'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import {
  AlertTriangle,
  CheckCircle2,
  HeartPulse,
  Info,
  XCircle,
} from 'lucide-react'
import { formatDateTime } from '@/lib/formatAgent'
import {
  ASSESSMENT_SCOPE,
  TOOLKIT_CARD_DESCRIPTION,
  TOOLKIT_CARD_TITLE,
  assessmentChecks,
  checkStatusLabel,
  checkStatusVariant,
  formatSourceCheck,
  formatToolkitScore,
  hasFullChecklist,
  normaliseCheckStatus,
  normaliseToolkitStatus,
  primaryFinding,
  toolkitStatusLabel,
  toolkitStatusMeaning,
  toolkitStatusVariant,
} from '@/lib/toolkitHealth'
import { cn } from '@/lib/utils'

const STATUS_ICONS = {
  PASS: CheckCircle2,
  WARN: AlertTriangle,
  FAIL: XCircle,
}

const CHECK_STATUS_ICONS = {
  PASS: CheckCircle2,
  WARN: AlertTriangle,
  FAIL: XCircle,
  INFO: Info,
}

function StatusBadge({ status }) {
  const normalised = normaliseToolkitStatus(status)
  if (!normalised) {
    return <Badge variant="secondary">No data</Badge>
  }
  const Icon = STATUS_ICONS[normalised]
  return (
    <Badge variant={toolkitStatusVariant(normalised)} className="gap-1">
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {toolkitStatusLabel(normalised)}
    </Badge>
  )
}

function CountRow({ label, value, variant }) {
  if (value == null) return null
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      {variant ? (
        <Badge variant={variant}>{value}</Badge>
      ) : (
        <span className="font-medium tabular-nums">{value}</span>
      )}
    </div>
  )
}

function IssueStatusBadge({ status }) {
  const normalised = normaliseToolkitStatus(status)
  if (!normalised) return <span>—</span>
  return (
    <Badge variant={toolkitStatusVariant(normalised)} className="shrink-0">
      {toolkitStatusLabel(normalised)}
    </Badge>
  )
}

function CheckStatusBadge({ status }) {
  const normalised = normaliseCheckStatus(status)
  const Icon = normalised ? CHECK_STATUS_ICONS[normalised] : null
  return (
    <Badge variant={checkStatusVariant(status)} className="shrink-0 gap-1">
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {checkStatusLabel(status)}
    </Badge>
  )
}

function SummaryStats({ data, checksRun }) {
  const items = [
    { label: 'checks', value: checksRun ?? data.checks_run, variant: null },
    { label: 'passed', value: data.passed, variant: 'success' },
    { label: 'warnings', value: data.warnings, variant: 'warning' },
    { label: 'failures', value: data.failures, variant: 'destructive' },
    { label: 'info', value: data.info, variant: 'secondary' },
  ].filter((item) => item.value != null && item.value !== '')

  if (!items.length) return null

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) =>
        item.variant ? (
          <Badge key={item.label} variant={item.variant}>
            {item.value} {item.label}
          </Badge>
        ) : (
          <span key={item.label} className="text-sm text-muted-foreground tabular-nums">
            {item.value} {item.label}
          </span>
        )
      )}
    </div>
  )
}

function AssessmentScopeList() {
  return (
    <div className="rounded-md border border-dashed border-border p-3">
      <p className="text-xs font-medium text-muted-foreground mb-2">
        This assessment includes
      </p>
      <ul className="text-sm text-muted-foreground list-disc list-inside space-y-0.5">
        {ASSESSMENT_SCOPE.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  )
}

function ChecksPerformedTable({ checks, checksRun }) {
  if (!checks.length) return null
  const fullList = hasFullChecklist({ checks, checks_run: checksRun })
  return (
    <div className="pt-2">
      <p className="text-xs font-medium text-muted-foreground mb-2">
        Check results ({checks.length}
        {checksRun ? ` of ${checksRun} reported` : ''})
      </p>
      {!fullList && (
        <p className="text-sm text-amber-600 mb-2">
          Partial upload — re-run the Toolkit summary script on this device to
          refresh the full checklist.
        </p>
      )}
      <div className="rounded-md border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
              <th className="px-3 py-2 font-medium">Check</th>
              <th className="px-3 py-2 font-medium">Result</th>
              <th className="px-3 py-2 font-medium">Detail</th>
            </tr>
          </thead>
          <tbody>
            {checks.map((check, index) => (
              <tr
                key={`${check.check}-${index}`}
                className="border-b border-border last:border-0 align-top"
              >
                <td className="px-3 py-2 font-medium whitespace-nowrap">
                  {check.check || 'Check'}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <CheckStatusBadge status={check.status} />
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {check.detail || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function FindingBlock({ finding }) {
  if (!finding) return null
  return (
    <div className="rounded-md border border-border bg-muted/30 p-3 space-y-1">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium">{finding.title}</p>
        {finding.status && <IssueStatusBadge status={finding.status} />}
      </div>
      {finding.detail && (
        <p className="text-sm text-muted-foreground">{finding.detail}</p>
      )}
    </div>
  )
}

function toolkitPillVariant(status) {
  const normalised = normaliseToolkitStatus(status)
  if (normalised === 'PASS') return 'ready'
  if (normalised === 'WARN') return 'pending'
  if (normalised === 'FAIL') return 'destructive'
  return 'inactive'
}

export function BekatToolkitSummaryCard({
  summary,
  collectedAt,
  className,
  open: controlledOpen,
  onOpenChange,
  panelId = 'overview-toolkit-summary',
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : uncontrolledOpen

  function handleOpenChange(next) {
    if (!isControlled) setUncontrolledOpen(next)
    onOpenChange?.(next)
  }

  const id = useId()
  const data = summary || {}
  const status = data.overall_status
  const hasData = Boolean(normaliseToolkitStatus(status) || data.checks_run != null)
  const checks = assessmentChecks(data)
  const fullChecklist = hasFullChecklist(data)
  const when = collectedAt || data.collected_at
  const finding = primaryFinding(data)
  const meaning = toolkitStatusMeaning(status)

  const summaryBits = []
  if (hasData) summaryBits.push(toolkitStatusLabel(status))
  if (data.score != null) summaryBits.push(`Score ${formatToolkitScore(data.score)}`)
  if (when) summaryBits.push(`Last assessed ${formatDateTime(when)}`)
  if (!hasData) summaryBits.push('No assessment data yet')

  return (
    <div id={panelId} className={cn('scroll-mt-4', className)}>
      <CollapsePanel
        id={`toolkit-summary-${id}`}
        title={TOOLKIT_CARD_TITLE}
        collapsedSummary={summaryBits.join(' · ')}
        open={open}
        onOpenChange={handleOpenChange}
        pill={hasData ? toolkitStatusLabel(status) : 'No data'}
        pillVariant={hasData ? toolkitPillVariant(status) : 'inactive'}
        bodyClassName="space-y-3"
      >
      <CollectedAtLine collectedAt={when} prefix="Last assessed" />
      <p className="text-xs text-muted-foreground leading-relaxed flex items-start gap-2">
        <HeartPulse className="h-4 w-4 text-primary shrink-0 mt-0.5" />
        <span>{TOOLKIT_CARD_DESCRIPTION}</span>
      </p>

      {!hasData ? (
        <p className="text-sm text-muted-foreground">
          No assessment data yet. Schedule the Bekat Toolkit summary script from Atera on this
          device.
        </p>
      ) : (
        <>
          <div className="flex justify-between gap-4 text-sm">
            <span className="text-muted-foreground">Assessment result</span>
            <StatusBadge status={status} />
          </div>

          {meaning && <p className="text-sm text-muted-foreground">{meaning}</p>}

          {finding && <FindingBlock finding={finding} />}

          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-muted/20 px-3 py-2">
            <span className="text-sm text-muted-foreground">Toolkit score</span>
            <span className="text-lg font-semibold tabular-nums">
              {formatToolkitScore(data.score)}
            </span>
          </div>

          {checks.length > 0 ? (
            <SummaryStats data={data} checksRun={checks.length} />
          ) : (
            <>
              <CountRow label="Checks run" value={data.checks_run} />
              <CountRow label="Passed" value={data.passed} variant="success" />
              <CountRow label="Warnings" value={data.warnings} variant="warning" />
              <CountRow label="Failures" value={data.failures} variant="destructive" />
              {data.info != null && data.info > 0 && (
                <CountRow label="Info" value={data.info} />
              )}
            </>
          )}

          <div className="text-sm">
            <span className="text-muted-foreground">Based on: </span>
            <span className="font-medium">
              {formatSourceCheck(data.script_name, data.script_version)}
            </span>
          </div>

          {data.interpretation && finding?.detail !== data.interpretation && (
            <p className="text-sm text-muted-foreground border-t border-border pt-3">
              {data.interpretation}
            </p>
          )}

          {!fullChecklist && checks.length > 0 && <AssessmentScopeList />}

          {checks.length > 0 && (
            <ChecksPerformedTable checks={checks} checksRun={data.checks_run} />
          )}

          {checks.length === 0 && (
            <div className="space-y-3">
              <AssessmentScopeList />
              <p className="text-sm text-muted-foreground">
                No per-check results were uploaded yet. Re-run the Bekat Toolkit summary script
                on this device (script v1.1 or newer).
              </p>
            </div>
          )}

          <p className="text-xs text-muted-foreground border-t border-border pt-3">
            Toolkit score starts at 100 and subtracts 8 per warning and 20 per failure. Run full
            Bekat Toolkit scripts in Atera for detailed diagnostics.
          </p>
        </>
      )}
      </CollapsePanel>
    </div>
  )
}
